import { PromoToolSlot } from '@/types/amplience';
import { getDomain } from './domain';
import { isScheduledItemExpired } from './isScheduledItemExpired';

const ACTIVE_PROMO_CODE_KEY = 'activatedVoucher';
const ACTIVE_PROMO_NAME_KEY = 'activatedPromo';
const PROMO_POPUP_CLOSED_KEY = 'promoPopupClosed';
// Marks that the active voucher was activated via a `?couponCode=` URL param.
// Set as a session cookie so the "duration of the session" scope falls out for free.
const URL_COUPON_MARKER_KEY = 'activatedVoucherFromUrl';
const MAX_CLOSED_PROMOS = 5;

const getCookieKeyWithEnv = (key: string) => {
  const hostname = window.location.hostname;

  if (hostname.startsWith('www.spreadshirt')) return key;
  if (hostname.startsWith('next.spreadshirt')) return `${key}-staging`;
  return `${key}-dev`;
};

const getCookieValue = (key: string): string | null | undefined => {
  if (typeof window === 'undefined') return null;
  try {
    const cookie = document.cookie.split('; ').find((row) => row.startsWith(`${key}=`));
    return cookie ? cookie.split('=')[1] : null;
  } catch {
    return null;
  }
};

const setCookie = (key: string, value: string | null, expires: Date | null) => {
  if (typeof window === 'undefined') return;
  try {
    const domain = getDomain();
    if (value) {
      // Omit `expires` entirely when none is given → a proper browser session cookie.
      const expiresPart = expires ? `expires=${expires.toUTCString()}; ` : '';
      document.cookie = `${key}=${value}; path=/; ${expiresPart}Domain=${domain}`;
    } else {
      document.cookie = `${key}=; path=/; max-age=0; Domain=${domain}`;
    }
  } catch {
    // do nothing
  }
};

export const isPromoExired = (promoSlot: PromoToolSlot | null): boolean => {
  return promoSlot && (promoSlot.content._meta.lifecycle || promoSlot.content._meta.edition)
    ? isScheduledItemExpired(promoSlot.content._meta)
    : true;
};

export const getActivePromoCode = () => {
  return getCookieValue(ACTIVE_PROMO_CODE_KEY);
};

export const setActivePromoCode = (code: string | null, expires: Date | null) => {
  setCookie(ACTIVE_PROMO_CODE_KEY, code, expires);
  // A regular (CMS/popup) activation is not URL-sourced, so drop any URL marker.
  setCookie(URL_COUPON_MARKER_KEY, null, null);
};

export const isUrlCoupon = (): boolean => {
  return getCookieValue(URL_COUPON_MARKER_KEY) === '1';
};

// Stores a coupon activated from a `?couponCode=` URL param: the code goes into the
// existing `activatedVoucher` cookie, but session-scoped, alongside a session marker.
export const setUrlCoupon = (code: string) => {
  setCookie(ACTIVE_PROMO_CODE_KEY, code, null);
  setCookie(URL_COUPON_MARKER_KEY, '1', null);
};

export const clearActivePromoCode = () => {
  setCookie(ACTIVE_PROMO_CODE_KEY, null, null);
  setCookie(URL_COUPON_MARKER_KEY, null, null);
};

export const getActivePromoName = () => {
  return getCookieValue(ACTIVE_PROMO_NAME_KEY);
};

export const setActivePromoName = (name: string | null, expires: Date | null) => {
  setCookie(ACTIVE_PROMO_NAME_KEY, name, expires);
};

const getClosedPromoCodes = (): string[] => {
  const keyWithEnv = getCookieKeyWithEnv(PROMO_POPUP_CLOSED_KEY);
  const value = getCookieValue(keyWithEnv);
  if (!value) return [];
  try {
    return value.split(',').filter(Boolean);
  } catch {
    return [];
  }
};

export const isPromoPopupClosed = (promoCode: string): boolean => {
  const closedCodes = getClosedPromoCodes();
  return closedCodes.includes(promoCode);
};

export const setPromoPopupClosed = (promoCode: string, expires: Date | null) => {
  const closedCodes = getClosedPromoCodes();

  if (closedCodes.includes(promoCode)) return;
  closedCodes.push(promoCode);
  if (closedCodes.length > MAX_CLOSED_PROMOS) {
    closedCodes.shift();
  }
  const keyWithEnv = getCookieKeyWithEnv(PROMO_POPUP_CLOSED_KEY);
  setCookie(keyWithEnv, closedCodes.join(','), expires);
};

/**
 * Returns a 6 month expiry date for the promo popup cookie.
 * Note: Safari ITP may set cookies to 7 days for inactive users,
 * so some Safari users might see the popup again after 7 days of inactivity.
 */
export const getPromoExpiryDate = (): Date => {
  const expiryDate = new Date();
  expiryDate.setMonth(expiryDate.getMonth() + 6);
  return expiryDate;
};
